"use client"

/**
 * Live preview surface for /tools/seeding-calculator. Verdict banner +
 * best-window date + soil/germ pills + 12-month NOW calendar. Updates on
 * every input change in the form, no submit gate.
 *
 * Visual treatment ports the Claude Design polish for this tool — inline
 * styles + S_* tokens, matching the sibling AerationPreview pattern.
 */

import { GRASS_CHARACTERISTICS } from "@/data/grass-characteristics"
import {
  fmtRange,
  type SeedingVerdict,
  type SeedingViewPlan,
} from "@/lib/seeding-plan"
import type { SeedingResult } from "@/lib/seeding-calculator"
import { YearCalendar } from "@/components/tools/shared/year-calendar"

const S_INK = "#111827"
const S_INK2 = "#374151"
const S_RULE = "#e5e7eb"
const S_BRAND = "#16a34a"
const S_BRAND_700 = "#15803d"
const S_BRAND_50 = "#f0fdf4"
const S_BRAND_100 = "#dcfce7"
const S_AMBER = "#f59e0b"
const S_AMBER_50 = "#fffbeb"
const S_AMBER_700 = "#b45309"
const S_DANGER = "#dc2626"
const S_DANGER_50 = "#fef2f2"
const S_BG_ALT = "#f9fafb"
const S_MUTED = "#6b7280"

interface VerdictPalette {
  fg: string
  bg: string
  accent: string
}

function palette(verdict: SeedingVerdict): VerdictPalette {
  switch (verdict) {
    case "go":
    case "go-secondary":
      return { fg: S_BRAND_700, bg: S_BRAND_50, accent: S_BRAND }
    case "closing":
    case "soon":
      return { fg: S_AMBER_700, bg: S_AMBER_50, accent: S_AMBER }
    case "too-late":
      return { fg: S_DANGER, bg: S_DANGER_50, accent: S_DANGER }
    case "wait":
    default:
      return { fg: S_INK2, bg: S_BG_ALT, accent: S_MUTED }
  }
}

export interface SeedingPreviewProps {
  plan: SeedingViewPlan
  result: SeedingResult
  /** Optional lawn size in sq ft for the inline lbs estimate. */
  lawnSize?: number
  /** Compact mobile-above-fold layout. */
  compact?: boolean
}

export function SeedingPreview({
  plan,
  result,
  lawnSize,
  compact = false,
}: SeedingPreviewProps) {
  const pal = palette(plan.verdict)
  const today = new Date()
  const thisMonth = today.getMonth()

  if (compact) {
    return (
      <div
        className="rounded-xl border border-gray-200 bg-white shadow-elevation-1"
        style={{ padding: 14, color: S_INK }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
          <span
            style={{
              fontSize: 10.5,
              fontWeight: 700,
              color: pal.fg,
              background: pal.bg,
              padding: "3px 8px",
              borderRadius: 9999,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}
          >
            {plan.pillLabel}
          </span>
          <span style={{ fontSize: 11, color: S_MUTED }}>· {result.grassType}</span>
        </div>
        <div
          style={{
            fontSize: 20,
            fontWeight: 800,
            color: S_INK,
            lineHeight: 1.15,
            letterSpacing: "-0.02em",
            marginBottom: 4,
          }}
        >
          {plan.bestWindow
            ? fmtRange(new Date(plan.bestWindow.optimalStart), new Date(plan.bestWindow.optimalEnd))
            : "—"}
        </div>
        <div style={{ fontSize: 12.5, color: S_INK2, lineHeight: 1.5, marginBottom: 10 }}>
          {plan.detail}
        </div>
        {plan.months.length > 0 && (
          <YearCalendar
            variant="compact"
            months={plan.months}
            thisMonth={thisMonth}
            bestMonth={plan.bestMonth}
            ringColors={{ today: S_INK, best: S_BRAND_700 }}
            ariaLabel="12-month seeding window calendar"
          />
        )}
      </div>
    )
  }

  const rate = GRASS_CHARACTERISTICS[result.grassType]?.seedingRate
  const hasSize = typeof lawnSize === "number" && lawnSize > 0
  const sizeMul = hasSize ? lawnSize / 1000 : 0

  return (
    <div
      className="rounded-2xl border border-gray-200 bg-white shadow-elevation-2"
      style={{
        padding: "24px 24px 22px",
        display: "flex",
        flexDirection: "column",
        gap: 20,
        color: S_INK,
      }}
    >
      {/* Verdict banner */}
      <div
        style={{
          background: pal.bg,
          borderRadius: 12,
          padding: "14px 16px",
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
        }}
      >
        <span
          style={{
            width: 32,
            height: 32,
            borderRadius: 16,
            flexShrink: 0,
            background: "#fff",
            color: pal.accent,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {plan.verdict === "too-late" ? (
            <XIcon size={16} />
          ) : plan.verdict === "go" || plan.verdict === "go-secondary" ? (
            <CheckIcon size={16} />
          ) : plan.verdict === "closing" || plan.verdict === "soon" ? (
            <AlertIcon size={16} />
          ) : (
            <ClockIcon size={16} />
          )}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: pal.fg,
              marginBottom: 2,
              lineHeight: 1.25,
            }}
          >
            {plan.headline}
          </div>
          <div style={{ fontSize: 13, color: S_INK2, lineHeight: 1.55 }}>{plan.detail}</div>
        </div>
      </div>

      {/* Best-window date */}
      {plan.bestWindow && (
        <div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.05em",
              textTransform: "uppercase",
              color: S_BRAND_700,
              marginBottom: 8,
            }}
          >
            {plan.daysLeft !== null ? "You’re in this window" : "Your next window"}
            {plan.bestWindowKind === "secondary" && (
              <span style={{ color: S_MUTED, marginLeft: 8 }}>· spring backup</span>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
            <span
              style={{
                fontSize: 30,
                fontWeight: 800,
                lineHeight: 1.1,
                letterSpacing: "-0.025em",
                color: S_INK,
              }}
            >
              {fmtRange(
                new Date(plan.bestWindow.optimalStart),
                new Date(plan.bestWindow.optimalEnd),
              )}
            </span>
            <span style={{ fontSize: 13, color: S_MUTED, fontWeight: 500 }}>
              {(() => {
                const start = new Date(plan.bestWindow.optimalStart)
                const end = new Date(plan.bestWindow.optimalEnd)
                const days = Math.max(1, Math.round((end.getTime() - start.getTime()) / 86400000))
                const yearSuffix =
                  start.getFullYear() !== new Date().getFullYear()
                    ? `${start.getFullYear()} · `
                    : ""
                return `${yearSuffix}${days} days`
              })()}
            </span>
          </div>
          <div style={{ display: "flex", gap: 16, marginTop: 10, flexWrap: "wrap" }}>
            <span
              style={{
                fontSize: 12.5,
                color: S_MUTED,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <ThermIcon size={13} /> Soil target: {plan.soilTemp}
            </span>
            <span
              style={{
                fontSize: 12.5,
                color: S_MUTED,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <ClockIcon size={13} /> Germination: {plan.germDays}
            </span>
          </div>
          {hasSize && rate && (
            <div
              style={{
                marginTop: 12,
                fontSize: 12.5,
                color: S_INK2,
                background: S_BRAND_50,
                border: `1px solid ${S_BRAND_100}`,
                borderRadius: 8,
                padding: "8px 10px",
                lineHeight: 1.5,
              }}
            >
              <strong style={{ color: S_BRAND_700 }}>
                ≈ {Math.ceil(sizeMul * rate.newLbsPer1000Min)}–
                {Math.ceil(sizeMul * rate.newLbsPer1000Max)} lbs
              </strong>{" "}
              new lawn, or{" "}
              <strong style={{ color: S_BRAND_700 }}>
                {Math.ceil(sizeMul * rate.overseedLbsPer1000Min)}–
                {Math.ceil(sizeMul * rate.overseedLbsPer1000Max)} lbs
              </strong>{" "}
              overseed for {lawnSize.toLocaleString()} sq ft.
            </div>
          )}
          {hasSize && !rate && (
            <div
              style={{
                marginTop: 12,
                fontSize: 12.5,
                color: S_AMBER_700,
                background: S_AMBER_50,
                border: `1px solid ${S_AMBER}`,
                borderRadius: 8,
                padding: "8px 10px",
                lineHeight: 1.5,
              }}
            >
              {result.grassType} is sodded or plugged. Seed isn’t a reliable option.
              Plan on sod for your {lawnSize.toLocaleString()} sq ft, or plugs on
              12-inch centers.
            </div>
          )}
        </div>
      )}

      <div style={{ height: 1, background: S_RULE }} />

      {/* 12-month calendar */}
      <div>
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: S_INK2,
            marginBottom: 12,
          }}
        >
          Year at a glance
        </div>
        {plan.months.length > 0 ? (
          <YearCalendar
            variant="full"
            months={plan.months}
            thisMonth={thisMonth}
            bestMonth={plan.bestMonth}
            ringColors={{ today: S_INK, best: S_BRAND_700 }}
            todayDot
            ariaLabel="12-month seeding window calendar"
          />
        ) : (
          <div style={{ fontSize: 13, color: S_MUTED }}>Calendar unavailable.</div>
        )}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            marginTop: 14,
            fontSize: 11.5,
            color: S_MUTED,
            flexWrap: "wrap",
          }}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 12, height: 12, borderRadius: 3, background: S_BRAND }} />
            Optimal
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span
              style={{ width: 12, height: 12, borderRadius: 3, background: S_BRAND_100 }}
            />
            Workable
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span
              style={{
                width: 12,
                height: 12,
                borderRadius: 3,
                background: S_BG_ALT,
                border: `1px solid ${S_RULE}`,
              }}
            />
            Don’t seed
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 6, height: 6, borderRadius: 3, background: S_INK }} />
            Today
          </span>
        </div>
      </div>
    </div>
  )
}

function CheckIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12l4 4 10-10" />
    </svg>
  )
}
function XIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  )
}
function AlertIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3l10 18H2L12 3z" />
      <path d="M12 10v5M12 18v0.5" />
    </svg>
  )
}
function ClockIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  )
}
function ThermIcon({ size = 13 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14 14V5a2 2 0 10-4 0v9a4 4 0 104 0z" />
      <circle cx="12" cy="17" r="1.5" fill="currentColor" />
    </svg>
  )
}
